import {
  Button,
  ChevronRightIcon,
  ContentReferences,
  DeleteConfirmButton,
  DevDetailsButton,
  Drawer,
  DrawerProps,
  Flex,
  InputContainer,
  MarkdownEditor,
  Menu,
  PencilIcon,
  PlusIcon,
  Spacer,
  Text,
  TextField,
  toaster,
} from 'components'
import { useAlert, useCurrentUser } from 'components/providers'
import { isEqual } from 'lodash-es'
import { MouseEvent, useEffect, useState } from 'react'
import { IContent } from 'types'
import {
  createDoc,
  deleteDoc,
  getDocById,
  getDocs,
  updateDoc,
  useDoc,
  usePermission,
  usePrefCurrentWorkspace,
  useTheme,
} from 'utils'
import {
  makeEmptyChildContent,
  useMutateContentRecords,
  useTextContent,
} from '../../utils/contentUtils'

interface Props extends Omit<DrawerProps, 'children' | 'onClose'> {
  id?: string
  onDone?: (body?: IContent) => void
  onDelete?: (body?: IContent) => void
  onTextSelect?: (childDoc: IContent) => void
}

export default function ManageTextDrawer({ id, onTextSelect, onDone, onDelete, ...rest }: Props) {
  const permission = usePermission()
  const [workspaceId] = usePrefCurrentWorkspace()
  const user = useCurrentUser()
  const { theme } = useTheme()
  const { confirm } = useAlert()
  const { content } = useTextContent()
  const { updateContentRecord, removeContentRecord } = useMutateContentRecords()
  const [textDoc] = useDoc('content', id)
  const [draftDoc, setDraftDoc] = useState<IContent | null>(null)
  const [parentContent, setParentContent] = useState<IContent | null>(null)
  const [childContents, setChildContents] = useState<IContent[]>([])

  const isViewer = permission.content === 'viewer'
  const isDirty = !isEqual(draftDoc, textDoc)

  useEffect(() => {
    setParentContent(null)
    setChildContents([])
    setDraftDoc(textDoc || null)

    const fetchParentAndChildren = async (contentItem: IContent) => {
      if (contentItem.parentId) {
        const parent = await getDocById('content', contentItem.parentId)
        if (parent) {
          setParentContent(parent)
        }
      }

      const children = await getDocs({
        collection: 'content',
        constraints: [
          { field: 'workspaceId', op: '==', value: workspaceId },
          { field: 'parentId', op: '==', value: contentItem.id },
        ],
      })
      setChildContents(children.data)
    }

    if (textDoc) {
      fetchParentAndChildren(textDoc)
    }
  }, [textDoc, workspaceId])

  async function handleDone(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    if (!isDirty) return onDone?.(textDoc || undefined)
    if (!draftDoc || !user) return

    try {
      const updated = await updateDoc(user.id, 'content', draftDoc)
      if (!updated.parentId) updateContentRecord(updated)
      onDone?.(updated)
    } catch (error) {
      console.error('Error updating document:', error)
      toaster.create({ type: 'error', description: 'An error occurred. Please try again.' })
    }
  }

  async function handleDelete() {
    if (!textDoc) return

    try {
      await deleteDoc('content', textDoc.id)
      if (textDoc.parentId) {
        const parent = content?.find(doc => doc.id === textDoc.parentId)
        if (parent) updateContentRecord({ ...parent, childCount: (parent.childCount ?? 0) - 1 })
      } else {
        removeContentRecord(textDoc.id)
      }
      onDelete?.(textDoc)
    } catch (error) {
      console.error('Error deleting content:', error)
      toaster.create({ type: 'error', description: 'Failed to delete content' })
    }
  }

  async function handleCancel() {
    if (isDirty) {
      const confirmed = await confirm('Discard changes?')
      if (!confirmed) return
    }
    onDone?.(textDoc || undefined)
  }

  return (
    <Drawer
      onClose={handleCancel}
      size="md"
      headerIcon={<PencilIcon />}
      header="Edit Content"
      headerActions={
        <>
          <DevDetailsButton
            items={[
              {
                collection: 'content',
                id: textDoc?.id,
                label: 'Content',
                type: 'db',
              },
            ]}
          />
          <Menu
            _hover={{ color: theme === 'light' ? 'primary.500' : undefined }}
            buttonLabel="Actions"
            variant="ghost"
            menuProps={{ positioning: { placement: 'bottom-start' } }}
            options={[
              {
                type: 'custom',
                label: (
                  <DeleteConfirmButton
                    onDelete={handleDelete}
                    width="100%"
                    borderRadius="none"
                    description={`${
                      textDoc?.childCount ? 'This will delete all sub content. Also, ' : ''
                    }This will prevent templates from using this content.`}
                  />
                ),
              },
            ]}
          />
        </>
      }
      footer={
        <>
          {isDirty && (
            <Button onClick={handleCancel} variant="outline">
              Cancel
            </Button>
          )}
          <Spacer />
          <Button palette="primary" onClick={handleDone}>
            {isDirty ? 'Save' : 'Done'}
          </Button>
        </>
      }
      footerProps={{ p: 4 }}
      {...rest}
    >
      <Flex gap={5} direction={'column'} mt={3} height={'95%'}>
        <Flex direction={'column'} gap={2}>
          {parentContent && (
            <Flex justify={'center'} gap={1}>
              <Text textStyle={'bodySmall'} opacity={0.7} color={'rgba(23, 23, 23, 0.6)'}>
                Sub content of
              </Text>
              <Text
                textStyle={'bodySmall'}
                opacity={0.7}
                color={'rgba(23, 23, 23, 0.6)'}
                {...(onTextSelect && {
                  as: 'u',
                  cursor: 'pointer',
                  onClick: () => onTextSelect?.(parentContent),
                })}
              >
                {parentContent.title}
              </Text>
            </Flex>
          )}
          <TextField
            label="Name"
            disabled={isViewer || !draftDoc}
            key={draftDoc?.id}
            id="smallTitle"
            placeholder="Please enter name for content"
            textStyle={'bodyLarge'}
            fontWeight={'500'}
            value={draftDoc?.title || ''}
            onChange={e => setDraftDoc(s => s && { ...s, title: e.target.value })}
            autoFocus
          />
        </Flex>
        <InputContainer overflow="hidden" label="Value">
          <MarkdownEditor
            containerProps={{ maxHeight: '50vh' }}
            key={draftDoc?.id}
            hideBlockTypes
            value={draftDoc?.value?.en || ''}
            onChange={value => setDraftDoc(s => s && { ...s, value: { en: value } })}
            readOnly={isViewer || !draftDoc}
          />
        </InputContainer>
        {(childContents.length > 0 || onTextSelect) && (
          <Flex direction={'column'} gap={2}>
            <InputContainer label="Manage Sub Content">
              <Flex
                direction={'column'}
                px={4}
                border={'1px solid rgba(23, 23, 23, 0.20)'}
                borderRadius="sm"
              >
                {childContents.map(childContent => (
                  <Flex
                    key={childContent.id}
                    direction={'row'}
                    gap={1}
                    onClick={() => !isDirty && onTextSelect?.(childContent)}
                    cursor={isDirty ? 'not-allowed' : 'pointer'}
                    opacity={isDirty ? 0.5 : 1}
                    py={4}
                    borderBottomWidth={'1px solid rgba(23, 23, 23, 0.20)'}
                    align={'center'}
                  >
                    <Flex direction={'column'} gap={1}>
                      <Text
                        textStyle={'bodyLarge'}
                        fontStyle={childContent.title ? 'normal' : 'italic'}
                      >
                        {childContent.title || 'Draft'}
                      </Text>
                      <MarkdownEditor
                        readOnly
                        lineClamp={2}
                        value={childContent.value?.en || 'Enter a description...'}
                      />
                    </Flex>
                    <Spacer />
                    <ChevronRightIcon fontSize="md" />
                  </Flex>
                ))}
                {onTextSelect && (
                  <Flex
                    direction={'row'}
                    gap={1}
                    onClick={async () => {
                      if (isDirty || !textDoc) return

                      const childContent = await createDoc(
                        user!.id,
                        'content',
                        makeEmptyChildContent(workspaceId, textDoc.id)
                      )
                      updateContentRecord({ ...textDoc, childCount: (textDoc.childCount ?? 0) + 1 })
                      onTextSelect(childContent)
                    }}
                    cursor={isDirty ? 'not-allowed' : 'pointer'}
                    py={4}
                    align={'center'}
                    opacity={isDirty ? 0.4 : 0.7}
                  >
                    <PlusIcon fontSize="md" />
                    <Text textStyle={'bodyMedium'}>Add new sub-content</Text>
                    <Spacer />
                    <ChevronRightIcon fontSize="sm" />
                  </Flex>
                )}
              </Flex>
            </InputContainer>
          </Flex>
        )}
        {textDoc && <ContentReferences id={textDoc.id} type="content" />}
      </Flex>
    </Drawer>
  )
}
